import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { auth, db } from "@/services/firebase";
import { call, CALLABLES } from "@/services/callables";
import { readStoredLocale } from "@/i18n";
import * as authService from "@/services/auth";
import { useI18n } from "./I18nContext";
import type { Institution, Locale, MeResponse, User, UserRole } from "@/types";

/**
 * Who is signed in.
 *
 * Three facts, and the gaps between them matter:
 *
 *  - **Firebase** knows there is a session. It will happily keep one for a user
 *    who has since been disabled.
 *  - **`me`** knows what that session may do. Only the backend can say so, and
 *    when it refuses we sign out rather than leave a shell with no data.
 *  - **The institution document**, live. A Spontar admin can change a licence
 *    tier or lift a suspension while an owner is looking at the page, and the
 *    honest thing is for the page to change under them.
 *
 * `status` distinguishes "still asking" from "nobody". Without that, every
 * guarded route flashes the login screen on reload.
 */

export type AuthStatus = "loading" | "signedOut" | "signedIn";

interface AuthValue {
  status: AuthStatus;
  user: User | undefined;
  institution: Institution | undefined;
  firebaseUser: FirebaseUser | undefined;
  signIn: (identifier: string, password: string) => Promise<void>;
  changePassword: (password: string) => Promise<void>;
  requestPasswordReset: (identifier: string) => Promise<authService.ResetOutcome>;
  /** Saves a language choice to the account, so it outlives this browser. */
  updateLocale: (locale: Locale) => Promise<void>;
  /** Re-reads `me`. Called after a password change or a profile edit. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (roles: readonly UserRole[]) => boolean;
}

const AuthContext = createContext<AuthValue | undefined>(undefined);

export function AuthProvider({
  children,
  /** Tests inject a resolved state and skip Firebase entirely. */
  initialValue,
}: {
  children: ReactNode;
  initialValue?: Partial<AuthValue>;
}) {
  const { setLocale } = useI18n();
  const [status, setStatus] = useState<AuthStatus>(initialValue?.status ?? "loading");
  const [user, setUser] = useState<User | undefined>(initialValue?.user);
  const [institution, setInstitution] = useState<Institution | undefined>(
    initialValue?.institution
  );
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | undefined>(undefined);

  /**
   * Which account we have already applied a stored language for.
   *
   * `onAuthStateChanged` fires again on every token refresh, and applying the
   * profile locale each time would silently undo a language a teacher had just
   * chosen — a minute or an hour into their session, with no action of theirs
   * to explain it. Once per account is the correct dose.
   */
  const localeAppliedFor = useRef<string | undefined>(undefined);

  const injected = initialValue !== undefined;

  useEffect(() => {
    if (injected) return;

    return onAuthStateChanged(auth(), async (next) => {
      setFirebaseUser(next ?? undefined);

      if (!next) {
        localeAppliedFor.current = undefined;
        setUser(undefined);
        setStatus("signedOut");
        return;
      }

      try {
        const response = await call<undefined, MeResponse>(CALLABLES.me);
        setUser(response.user);
        setStatus("signedIn");

        /*
          Precedence: an explicit choice made in THIS browser, then the
          account, then the browser's own languages, then English.

          The account is what makes a new machine open in the right language,
          and it is what the backend reads when it sends an email or prints a
          credential slip. But a choice someone just made here outranks it —
          the write that saves it to the account is a round trip, and reloading
          during that second must not undo the click that started it.
        */
        const chosenHere = readStoredLocale();
        if (localeAppliedFor.current !== response.user.uid) {
          localeAppliedFor.current = response.user.uid;
          if (!chosenHere) setLocale(response.user.locale);
        }

        /*
          Reconciliation. Saving a switch to the account is one round trip, and
          a person who switches and immediately navigates can outrun it — the
          request is cancelled with the page. Rather than making them wait for
          a write they should never have to think about, the next load notices
          the disagreement and settles it in favour of what they chose.
        */
        if (chosenHere && chosenHere !== response.user.locale && !response.user.mustChangePassword) {
          void call(CALLABLES.updateOwnProfile, { locale: chosenHere }).catch(() => {
            // Still only a preference. It will be tried again on the next load.
          });
        }
      } catch {
        // A token Firebase accepts and the backend rejects — disabled, or no
        // institution. Signing out is the honest outcome.
        await firebaseSignOut(auth());
        setUser(undefined);
        setStatus("signedOut");
      }
    });
  }, [injected, setLocale]);

  const institutionId = user?.institutionId ?? undefined;

  useEffect(() => {
    if (injected || !institutionId) {
      if (!injected) setInstitution(undefined);
      return;
    }

    return onSnapshot(
      doc(db(), "institutions", institutionId),
      (snapshot) => {
        setInstitution(snapshot.exists() ? (snapshot.data() as Institution) : undefined);
      },
      () => {
        // A rules rejection here is not worth a toast. The header loses a name;
        // every page that needs the institution asks a callable, which fails
        // loudly and in words.
        setInstitution(undefined);
      }
    );
  }, [injected, institutionId]);

  const refresh = useCallback(async () => {
    const response = await call<undefined, MeResponse>(CALLABLES.me);
    setUser(response.user);
    setStatus("signedIn");
  }, []);

  const value = useMemo<AuthValue>(() => {
    const base: AuthValue = {
      status,
      user,
      institution,
      firebaseUser,
      signIn: async (identifier, password) => {
        await authService.signIn(identifier, password);
        // The auth listener loads `me` and flips `status`.
      },
      changePassword: async (password) => {
        await authService.completePasswordChange(password);
        await refresh();
      },
      requestPasswordReset: authService.requestPasswordReset,
      updateLocale: async (next) => {
        await call(CALLABLES.updateOwnProfile, { locale: next });
        // Kept locally too, so the header does not wait for a round trip and
        // does not flicker back if the write is slow.
        setUser((current) => (current ? { ...current, locale: next } : current));
      },
      refresh,
      signOut: async () => {
        await firebaseSignOut(auth());
      },
      hasRole: (roles) => (user ? roles.includes(user.role) : false),
    };
    return injected ? { ...base, ...initialValue } : base;
  }, [status, user, institution, firebaseUser, refresh, injected, initialValue]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
